/**
 * THE BELL.
 *
 * One job: say whether anything has happened, and get you to the record of it.
 *
 * ── NO POLLING ────────────────────────────────────────────────────────────
 *
 * It reads from the global notifications context, which loads ONCE on page
 * load and again when the tab regains focus. No interval.
 *
 * The earlier version asked the server every thirty seconds. That is a request
 * per user per half-minute forever, and ours are not cheap: every call runs
 * JWT verification, then actingContext validating the tier and company headers
 * against resolve_actable_tiers() IN THE DATABASE, then the query. Spending
 * that to learn "nothing happened" is the wrong trade for an MVP.
 *
 * Live push belongs on the SSE stream that already exists for messages, as an
 * event type rather than a second transport. Next step, not a different design.
 *
 * ── THE PANEL IS A PREVIEW, NOT THE PRODUCT ───────────────────────────────
 *
 * Five lines and a way through to Activity, which is the real surface. A
 * dropdown that tries to be the page ends up being a worse version of it.
 */

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { useNotifications } from "@/lib/context/NotificationsContext";
import "./notification-bell.css";

const PREVIEW = 5;

export function NotificationBell() {
  const navigate = useNavigate();
  const { notifications, unread, refresh, markRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement | null>(null);

  /* Close on an outside click. Without this the panel stays open behind every
     screen you navigate to, which reads as broken rather than sticky. */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    return () => document.removeEventListener("pointerdown", onDown);
  }, [open]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    /* Re-read on OPEN, so the one moment somebody actually looks is the one
       moment it is guaranteed fresh — without asking every thirty seconds for
       the rest of the day. */
    if (next) void refresh().then(() => void markRead());
  };

  const preview = notifications.slice(0, PREVIEW);

  return (
    <div className="nb" ref={boxRef}>
      <button type="button" className="nb-btn" onClick={toggle}
              aria-label={unread ? `${unread} unread notifications` : "Notifications"}>
        <svg width="17" height="17" viewBox="0 0 24 24" fill="none"
             stroke="currentColor" strokeWidth="2" strokeLinecap="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.7 21a2 2 0 0 1-3.4 0" />
        </svg>
        {unread > 0 && <span className="nb-dot">{unread > 9 ? "9+" : unread}</span>}
      </button>

      {open && (
        <div className="nb-panel" role="dialog" aria-label="Notifications">
          <div className="nb-h">Recent activity</div>

          {preview.length === 0 ? (
            <div className="nb-empty">
              Nothing yet. When another company acts on something of yours, it
              appears here.
            </div>
          ) : (
            <ul className="nb-list">
              {preview.map(n => (
                <li key={n.id}>
                  <button type="button" className={`nb-item${n.read ? "" : " unread"}`}
                          onClick={() => {
                            setOpen(false);
                            navigate(n.nav?.to ?? "/activity");
                          }}>
                    <span className="m">{n.msg}</span>
                    <span className="t">{when(n.created_at)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          <button type="button" className="nb-all"
                  onClick={() => { setOpen(false); navigate("/activity"); }}>
            See everything that has happened →
          </button>
        </div>
      )}
    </div>
  );
}

function when(iso: string): string {
  const mins = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (Number.isNaN(mins)) return "";
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.floor(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
